import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages = {
    en: { code: 'EN', name: t('languages.en') },
    fr: { code: 'FR', name: t('languages.fr') },
    de: { code: 'DE', name: t('languages.de') }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    // Sync language to backend (fire-and-forget)
    api.saveLanguage(lng).catch(() => {});
    setIsOpen(false);
  };

  const currentLang = languages[i18n.language] || languages.en;

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '8px 16px',
          background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
          border: '1px solid #ffffff4d',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: 'white',
          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
          justifyContent: 'space-between',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
        }}
      >
        <span>{currentLang.code}</span>
        <span style={{
          fontSize: '10px',
          transition: 'transform 0.2s',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
        }}>
          &#9660;
        </span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            background: 'white',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: '150px',
            zIndex: 1000,
            overflow: 'hidden',
            animation: 'slideDown 0.2s ease'
          }}
        >
          {Object.entries(languages).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => changeLanguage(code)}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: i18n.language === code ? '#f8fafc' : 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                fontWeight: '600',
                color: i18n.language === code ? '#3B82F6' : '#1e293b',
                textAlign: 'left',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                if (i18n.language !== code) {
                  e.currentTarget.style.background = '#f8fafc';
                }
              }}
              onMouseLeave={(e) => {
                if (i18n.language !== code) {
                  e.currentTarget.style.background = 'white';
                }
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontWeight: '700' }}>{lang.code}</span>
                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '400' }}>
                  {lang.name}
                </span>
              </div>
              {i18n.language === code && (
                <span style={{ marginLeft: 'auto', color: '#3B82F6', fontSize: '16px' }}>&#10003;</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
